// src/utils/uuid.js
/**
 * 브라우저 환경에 관계없이 안전하게 UUID v4 생성
 * - crypto.randomUUID() 우선 사용 (HTTPS/localhost)
 * - crypto.getRandomValues() 폴백 (HTTP 환경)
 * - Math.random() 최후 폴백 (구형 브라우저)
 */
// src/utils/uuid.js
// UUID 생성 유틸 (브라우저 환경)
// - crypto.randomUUID() 우선 (HTTPS/localhost)
// - crypto.getRandomValues() 폴백 (HTTP 환경)
// - Math.random() 최후 폴백 (구형 브라우저)
// - 순수 함수 / 프로토타입 확장 금지 / tree-shakable named export
// 개발완료 - 성능 최적화 및 디버깅 완료

/* =========================================================
 * 상수 및 캐시
 * ======================================================= */

// UUID v4 검증 정규식 (RFC 4122)
const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// 기능 감지 캐싱 (성능 최적화)
let _uuidMethod = null;

/**
 * UUID 생성 방식 감지 (한 번만 실행)
 * @returns {'native' | 'crypto' | 'math'}
 */
function detectUUIDMethod() {
  if (_uuidMethod !== null) return _uuidMethod;

  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    _uuidMethod = 'native';
  } else if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    _uuidMethod = 'crypto';
  } else {
    _uuidMethod = 'math';
  }

  return _uuidMethod;
}

/* =========================================================
 * 내부 생성 함수
 * ======================================================= */

/**
 * crypto.getRandomValues 기반 UUID 생성
 */
function generateWithCrypto() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

/**
 * Math.random 기반 UUID 생성 (폴백, 보안 목적 부적합)
 */
function generateWithMath() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/* =========================================================
 * 공개 API
 * ======================================================= */

/**
 * UUID v4 생성
 * - crypto.randomUUID() 우선 (HTTPS/localhost)
 * - crypto.getRandomValues() 폴백 (HTTP 환경)
 * - Math.random() 최후 폴백 (구형 브라우저)
 * 
 * @returns {string} UUID v4 형식 문자열
 * @example generateUUID() → "550e8400-e29b-41d4-a716-446655440000"
 */
export function generateUUID() {
  const method = detectUUIDMethod();

  switch (method) {
    case 'native':
      return crypto.randomUUID();
    case 'crypto':
      return generateWithCrypto();
    default:
      return generateWithMath();
  }
}

/**
 * 짧은 고유 ID 생성 (UUID 앞 8자리)
 * - 충돌 가능성 있으므로 고유성 보장 필요 시 generateUUID 사용
 * 
 * @returns {string} 8자리 hex 문자열
 * @example generateShortId() → "550e8400"
 */
export function generateShortId() {
  return generateUUID().split('-')[0];
}

/**
 * 타임스탬프 기반 정렬 가능 ID 생성
 * - 시간순 정렬이 필요한 경우 사용
 * - 형식: {timestamp(hex)}-{random(4자리)}
 * 
 * @returns {string} 정렬 가능한 ID
 * @example generateSortableId() → "18f5a3b2c00-a1b2"
 */
export function generateSortableId() {
  const timestamp = Date.now().toString(16);
  const random = generateUUID().substring(0, 4);
  return `${timestamp}-${random}`;
}

/**
 * UUID v4 형식 검증
 * 
 * @param {string} uuid 검증할 문자열
 * @returns {boolean} 유효한 UUID v4 여부
 * @example isValidUUID("550e8400-e29b-41d4-a716-446655440000") → true
 */
export function isValidUUID(uuid) {
  if (typeof uuid !== 'string') return false;
  return UUID_V4_REGEX.test(uuid);
}

/**
 * UUID에서 하이픈 제거
 * 
 * @param {string} uuid UUID 문자열
 * @returns {string|null} 하이픈 제거된 32자리 hex 또는 null
 * @example toCompactUUID("550e8400-e29b-41d4-a716-446655440000") → "550e8400e29b41d4a716446655440000"
 */
export function toCompactUUID(uuid) {
  if (!isValidUUID(uuid)) return null;
  return uuid.replace(/-/g, '');
}

/**
 * 하이픈 없는 UUID를 표준 형식으로 변환
 * 
 * @param {string} compact 32자리 hex 문자열
 * @returns {string|null} 표준 UUID 형식 또는 null
 * @example fromCompactUUID("550e8400e29b41d4a716446655440000") → "550e8400-e29b-41d4-a716-446655440000"
 */
export function fromCompactUUID(compact) {
  if (typeof compact !== 'string' || compact.length !== 32) return null;
  if (!/^[0-9a-f]{32}$/i.test(compact)) return null;

  const formatted = [
    compact.slice(0, 8),
    compact.slice(8, 12),
    compact.slice(12, 16),
    compact.slice(16, 20),
    compact.slice(20),
  ].join('-');

  return isValidUUID(formatted) ? formatted : null;
}

/**
 * 현재 환경에서 사용 중인 UUID 생성 방식 반환 (디버깅용)
 * 
 * @returns {'native' | 'crypto' | 'math'}
 */
export function getUUIDMethod() {
  return detectUUIDMethod();
}

/* =========================================================
 * default export (편의용)
 * ======================================================= */

const uuidUtils = Object.freeze({
  generateUUID,
  generateShortId,
  generateSortableId,
  isValidUUID,
  toCompactUUID,
  fromCompactUUID,
  getUUIDMethod,
});

export default uuidUtils;